import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  db,
  collection,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  doc,
  updateDoc,
  Timestamp,
} from '../firebase';

function NotificationBell({ user }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const panelRef = useRef(null);

  const uid = user?.uid;

  useEffect(() => {
    if (!uid) return;
    const q = query(
      collection(db, 'notifications'),
      where('userId', '==', uid),
      orderBy('createdAt', 'desc'),
      limit(40),
    );
    return onSnapshot(q, (snap) => {
      setRows(snap.docs.map((d) => ({ _id: d.id, ...d.data() })));
    });
  }, [uid]);

  const unread = useMemo(() => rows.filter((r) => !r.read).length, [rows]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, [open]);

  if (!uid) return null;

  const markRead = async (id) => {
    await updateDoc(doc(db, 'notifications', id), {
      read: true,
      readAt: Timestamp.now(),
    });
  };

  const onPick = async (n) => {
    if (!n.read) await markRead(n._id);
    setOpen(false);
    if (n.route === 'chat' && n.conversationId) {
      navigate(`/dashboard/chat?c=${encodeURIComponent(n.conversationId)}`);
      return;
    }
    if (n.route === 'todo') {
      navigate('/dashboard/todo');
      return;
    }
    if (n.route === 'calendar') {
      navigate('/dashboard/calendar');
      return;
    }
    navigate('/dashboard/profile');
  };

  const fmtTime = (ts) => {
    if (!ts?.toDate) return '';
    return ts.toDate().toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
  };

  return (
    <div className="haydi-notif-wrap" ref={panelRef}>
      <button
        type="button"
        className="haydi-notif-bell"
        aria-expanded={open}
        aria-haspopup="true"
        aria-label={t('notifications')}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
      >
        <span aria-hidden>🔔</span>
        {unread > 0 && <span className="haydi-notif-badge">{unread > 99 ? '99+' : unread}</span>}
      </button>
      {open && (
        <div className="haydi-notif-panel" role="menu">
          <div className="haydi-notif-panel__head">{t('notifications')}</div>
          <ul className="haydi-notif-list">
            {rows.length === 0 && (
              <li className="haydi-notif-empty">{t('notificationsEmpty')}</li>
            )}
            {rows.map((n) => (
              <li key={n._id}>
                <button
                  type="button"
                  className={`haydi-notif-item${n.read ? '' : ' haydi-notif-item--unread'}`}
                  onClick={() => onPick(n)}
                >
                  <span className="haydi-notif-item__title">{n.title || t('appName')}</span>
                  <span className="haydi-notif-item__body">{n.body}</span>
                  <span className="haydi-notif-item__meta">{fmtTime(n.createdAt)}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default NotificationBell;
